import React, { useEffect, useState } from 'react';
import { Modal, Form, Button, Row, Col } from 'react-bootstrap';
import { Input, InputNumber } from 'antd';
import { FiSave, FiUser, FiUsers } from 'react-icons/fi';
import * as slotSettingActions from '../../Redux/Actions/slotSettings';
import { toastMessage } from '../../utils/toast';
import { AlertBox, preventEnterSubmit } from './common';
import { TimeField, SwitchField } from './fields';
import {
    COUNTER_GENDERS, validateCounter, counterFieldFromMessage, getErrorMessage, hasErrors, toTime, formatTime12
} from './helpers';

const GENDER_ICONS = { MALE: <FiUser />, FEMALE: <FiUser />, BOTH: <FiUsers /> };
const GENDER_HINTS = { MALE: 'Male applicants only', FEMALE: 'Female applicants only', BOTH: 'Any applicant' };

const emptyForm = {
    name: '',
    gender: 'BOTH',
    startTime: '',
    endTime: '',
    hasBreak: false,
    breakFrom: '',
    breakTo: '',
    sortOrder: null,
    isActive: true
};

const toForm = (counter) => {
    if (!counter) return { ...emptyForm };
    return {
        name: counter.name || '',
        gender: counter.gender || 'BOTH',
        startTime: toTime(counter.startTime),
        endTime: toTime(counter.endTime),
        hasBreak: !!(counter.breakFrom || counter.breakTo),
        breakFrom: toTime(counter.breakFrom),
        breakTo: toTime(counter.breakTo),
        sortOrder: counter.sortOrder ?? null,
        isActive: counter.isActive !== false
    };
};

// counter: the one being edited, or null to add. slotMinutes: the center's slot length, for the hint.
const CounterFormModal = ({ show, counter, centerId, centerName, slotMinutes, onClose, onSaved }) => {
    const [form, setForm] = useState(toForm(counter));
    const [errors, setErrors] = useState({});
    const [serverError, setServerError] = useState('');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!show) return;
        setForm(toForm(counter));
        setErrors({});
        setServerError('');
    }, [show, counter]);

    const setField = (key, value, errorKey = key) => {
        setForm(prev => ({ ...prev, [key]: value }));
        if (errors[errorKey]) setErrors(prev => ({ ...prev, [errorKey]: undefined }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (saving) return;
        const formErrors = validateCounter(form);
        setErrors(formErrors);
        setServerError('');
        if (hasErrors(formErrors)) return;

        const body = {
            name: form.name.trim(),
            gender: form.gender,
            startTime: form.startTime,
            endTime: form.endTime,
            breakFrom: form.hasBreak ? form.breakFrom : null,
            breakTo: form.hasBreak ? form.breakTo : null,
            sortOrder: form.sortOrder === '' ? null : form.sortOrder,
            isActive: form.isActive
        };

        setSaving(true);
        const request = counter
            ? slotSettingActions.updateCounter(counter.id, body)
            : slotSettingActions.createCounter(centerId, body);
        request.then(res => {
            setSaving(false);
            toastMessage('success', counter ? `Counter ${body.name} saved` : `Counter ${body.name} added`);
            onSaved(res);
        }).catch(err => {
            setSaving(false);
            const message = getErrorMessage(err, 'Unable to save the counter. Please try again.');
            const field = counterFieldFromMessage(message);
            if (field) setErrors({ [field]: message });
            setServerError(message);
        });
    };

    const breakText = form.hasBreak && form.breakFrom && form.breakTo
        ? `, skipping the break (${formatTime12(form.breakFrom)} – ${formatTime12(form.breakTo)})`
        : '';
    const slotHint = form.startTime && form.endTime && form.startTime < form.endTime
        ? `This counter adds 1 seat to every ${slotMinutes || 'N'}-minute slot from ${formatTime12(form.startTime)} to ${formatTime12(form.endTime)}${breakText}. A slot's seats = the counters open for that whole slot.`
        : `Each counter adds 1 seat to every slot (the center's slot duration, ${slotMinutes || 'N'} minutes) it is open for, except during its break.`;

    return (
        <Modal show={show} onHide={saving ? undefined : onClose} centered size="lg" className="ss-modal"
            backdrop={saving ? 'static' : true} enforceFocus={false}>
            <Form onSubmit={handleSubmit} onKeyDown={preventEnterSubmit} noValidate>
                <Modal.Header closeButton={!saving}>
                    <Modal.Title as="h5">{counter ? `Edit counter ${counter.name}` : `Add counter${centerName ? ` · ${centerName}` : ''}`}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {serverError && <AlertBox title="Not saved" messages={serverError} onClose={() => setServerError('')} />}

                    <Row className="g-3">
                        <Col md={8}>
                            <Form.Label htmlFor="ct-name">Counter name <span className="text-danger">*</span></Form.Label>
                            <Input id="ct-name" value={form.name} maxLength={50} disabled={saving} placeholder="e.g. M1, F1, Counter 3"
                                status={errors.name ? 'error' : undefined} onChange={e => setField('name', e.target.value)} />
                            {errors.name
                                ? <span className="ss-field-error">{errors.name}</span>
                                : <span className="ss-hint">Must be unique within this center.</span>}
                        </Col>
                        <Col md={4}>
                            <Form.Label htmlFor="ct-order">Order</Form.Label>
                            <InputNumber id="ct-order" value={form.sortOrder} min={0} precision={0} disabled={saving} placeholder="0"
                                style={{ width: '100%' }} onChange={value => setField('sortOrder', value)} />
                            <span className="ss-hint">Lower numbers are listed first.</span>
                        </Col>

                        <Col xs={12}>
                            <div className="ss-section-label">Who can book this counter <span className="text-danger">*</span></div>
                            <div className="ss-gender-options" role="radiogroup" aria-label="Gender">
                                {COUNTER_GENDERS.map(option => (
                                    <button key={option.value} type="button" role="radio" aria-checked={form.gender === option.value}
                                        disabled={saving}
                                        className={`ss-gender-option is-${option.value.toLowerCase()} ${form.gender === option.value ? 'active' : ''}`}
                                        onClick={() => setField('gender', option.value)}>
                                        <span className="ss-gender-icon">{GENDER_ICONS[option.value]}</span>
                                        <span>
                                            <span className="ss-gender-title">{option.label}</span>
                                            <span className="ss-gender-hint">{GENDER_HINTS[option.value]}</span>
                                        </span>
                                    </button>
                                ))}
                            </div>
                            {errors.gender && <span className="ss-field-error">{errors.gender}</span>}
                        </Col>

                        <Col md={6}>
                            <Form.Label htmlFor="ct-start">Start time <span className="text-danger">*</span></Form.Label>
                            <TimeField id="ct-start" value={form.startTime} placeholder="Opens at" disabled={saving} error={errors.hours}
                                onChange={value => setField('startTime', value, 'hours')} />
                        </Col>
                        <Col md={6}>
                            <Form.Label htmlFor="ct-end">End time <span className="text-danger">*</span></Form.Label>
                            <TimeField id="ct-end" value={form.endTime} placeholder="Closes at" disabled={saving} error={errors.hours}
                                onChange={value => setField('endTime', value, 'hours')} />
                        </Col>
                        {errors.hours && <Col xs={12} className="mt-1"><span className="ss-field-error mt-0">{errors.hours}</span></Col>}

                        <Col xs={12}>
                            <div className="ss-switch-tile">
                                <div>
                                    <div className="ss-switch-title">Break</div>
                                    <span className="ss-hint mt-0">No slots are created during the break.</span>
                                </div>
                                <SwitchField id="ct-break" checked={form.hasBreak} disabled={saving}
                                    onChange={checked => setField('hasBreak', checked, 'break')} />
                            </div>
                        </Col>
                        {form.hasBreak && (
                            <>
                                <Col md={6}>
                                    <Form.Label htmlFor="ct-break-from">Break starts</Form.Label>
                                    <TimeField id="ct-break-from" value={form.breakFrom} placeholder="Break start" disabled={saving}
                                        error={errors.break} onChange={value => setField('breakFrom', value, 'break')} />
                                </Col>
                                <Col md={6}>
                                    <Form.Label htmlFor="ct-break-to">Break ends</Form.Label>
                                    <TimeField id="ct-break-to" value={form.breakTo} placeholder="Break end" disabled={saving}
                                        error={errors.break} onChange={value => setField('breakTo', value, 'break')} />
                                </Col>
                                {errors.break && <Col xs={12} className="mt-1"><span className="ss-field-error mt-0">{errors.break}</span></Col>}
                            </>
                        )}

                        <Col xs={12}>
                            <AlertBox variant="info" messages={slotHint} />
                        </Col>

                        <Col xs={12}>
                            <div className="ss-switch-tile">
                                <div>
                                    <div className="ss-switch-title">Active</div>
                                    <span className="ss-hint mt-0">Switch off to stop this counter's slots after its last booked day.</span>
                                </div>
                                <SwitchField id="ct-active" checked={form.isActive} disabled={saving}
                                    onChange={checked => setField('isActive', checked)} />
                            </div>
                        </Col>
                    </Row>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="light" onClick={onClose} disabled={saving}>Cancel</Button>
                    <Button variant="primary" type="submit" disabled={saving}>
                        <FiSave /> {saving ? 'Saving...' : counter ? 'Save changes' : 'Add counter'}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

export default CounterFormModal;
